/**
 * 👥 Team Service - Integração com API de Gerenciamento de Equipe
 * 
 * Service responsável por todas as chamadas relacionadas ao gerenciamento de equipe.
 * Integra com os endpoints criados em routes/team.js do backend.
 * 
 * @author Pagmus Team
 * @date 2025-10-30
 */

import apiService from './index';

const TeamService = {
  /**
   * 📊 Obter estatísticas da equipe
   * GET /api/team/stats
   */
  getStats: async () => {
    try {
      const response = await apiService.get('/api/team/stats');
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar estatísticas da equipe:', error);
      throw error;
    }
  },

  /**
   * 📋 Listar todos os membros da equipe
   * GET /api/team
   * @param {Object} filters - Filtros de busca
   * @param {string} filters.type - Tipo: 'all' | 'colaboradores' | 'vendedores' | 'afiliados'
   * @param {string} filters.search - Buscar por nome ou email
   * @param {string} filters.status - Status: 'all' | 'active' | 'pending'
   */
  getTeamMembers: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.type) params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      
      const queryString = params.toString();
      const url = queryString ? `/api/team?${queryString}` : '/api/team';
      
      const response = await apiService.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar membros da equipe:', error);
      throw error;
    }
  },

  /**
   * 🔍 Obter detalhes de um membro da equipe
   * GET /api/team/:userId
   * @param {number} userId - ID do usuário
   */
  getTeamMemberDetails: async (userId) => {
    try {
      const response = await apiService.get(`/api/team/${userId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar detalhes do membro:', error);
      throw error;
    }
  },

  /**
   * 🆕 Criar novo colaborador
   * POST /api/team/collaborator
   * @param {Object} collaboratorData - Dados do colaborador
   * @param {string} collaboratorData.name - Nome completo
   * @param {string} collaboratorData.email - Email
   * @param {string} collaboratorData.password - Senha
   * @param {number} collaboratorData.permission_group_id - ID do grupo (opcional)
   */
  createCollaborator: async (collaboratorData) => {
    try {
      const response = await apiService.post('/api/team/collaborator', collaboratorData);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao criar colaborador:', error);
      throw error;
    }
  },

  /**
   * 🆕 Criar novo vendedor exclusivo
   * POST /api/team/seller
   * @param {Object} sellerData - Dados do vendedor
   * @param {string} sellerData.name - Nome completo
   * @param {string} sellerData.email - Email
   * @param {string} sellerData.password - Senha
   * @param {number} sellerData.default_commission - Comissão padrão (%)
   * @param {Array<number>} sellerData.product_ids - IDs dos produtos (opcional)
   */
  createSeller: async (sellerData) => {
    try {
      const response = await apiService.post('/api/team/seller', sellerData);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao criar vendedor:', error);
      throw error;
    }
  },

  /**
   * 🆕 Criar novo afiliado direto
   * POST /api/team/affiliate
   * @param {Object} affiliateData - Dados do afiliado
   * @param {string} affiliateData.name - Nome completo
   * @param {string} affiliateData.email - Email
   * @param {string} affiliateData.password - Senha
   * @param {Array<Object>} affiliateData.products - Produtos e comissões
   * @param {number} affiliateData.products[].productId - ID do produto
   * @param {number} affiliateData.products[].commissionRate - Comissão (%)
   */
  createAffiliate: async (affiliateData) => {
    try {
      const response = await apiService.post('/api/team/affiliate', affiliateData);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao criar afiliado direto:', error);
      throw error;
    }
  }
};

export default TeamService;

